/**
 * Which recovery screen a worker is looking at, as a pure function.
 *
 * W8 draws eight conditions (`design/pages/worker/W8-recovery/JUDGEMENT.md`).
 * Deciding between them from inside a component would put the product's most
 * consequential branch — *is this punch recorded or not?* — in the place it is
 * hardest to test, so it lives here, table-tested, exactly like
 * `features/offline/selectors.ts` does for the six home states.
 *
 * Two rules the map exists to keep:
 *
 * 1. **Two of the eight are informational.** Offline-at-punch and GPS-denied
 *    mean *the punch is (or will be) recorded, keep working*; the screen keeps
 *    the nav, offers a `×`, and its primary action is "continue". A template
 *    that could only say "you are stuck" would be lying twice out of eight
 *    times (JUDGEMENT §A1).
 * 2. **Account lockout is a LOGIN refusal and is never rendered here**
 *    (JUDGEMENT §6.1.2). A locked worker cannot reach *Mes quarts*; drawing the
 *    authenticated shell around that message offers four doors that are shut.
 */
import type { ErrorCode } from '@saniplus/contracts';
import { WORKER_STATE_TONE, type StatusTone } from '@saniplus/ui';

import type { FailedItemSummary, QueuedPunchType, SubmitOutcome } from '../../offline';

export type RecoveryKind =
  /** Queued because there was no network. Informational. */
  | 'offline'
  /** A previous session was never closed (D-08). Blocking. */
  | 'staleOpen'
  /** Outside the 60/60 window, early or late. Blocking, but self-resolving when early. */
  | 'outsideWindow'
  /** Nothing scheduled. Blocking, and the next shift is the answer. */
  | 'noShift'
  /** This phone is not the bound one (D-06). Blocking. */
  | 'deviceNotRegistered'
  /** The worker refused the location permission. Informational — punch anyway. */
  | 'gpsDenied'
  /** A queued punch the server refused later, on sync. Blocking until acknowledged. */
  | 'syncRefused'
  /** Any other refusal the server made. Blocking. */
  | 'refused';

/** What the last direct punch attempt did. `null` until the worker taps. */
export interface RecoveryAttempt {
  readonly clientPunchUuid: string;
  readonly type: QueuedPunchType;
  readonly outcome: SubmitOutcome;
  readonly refusalCode: ErrorCode | null;
  /** The device instant the worker tapped at — what the screen calls "votre essai". */
  readonly attemptedAt: string;
}

/**
 * The worker tapped and the location permission is *denied* — not merely
 * absent. Shown before the punch so the primary action can be « Pointer
 * l'entrée quand même », which is what the mockup draws.
 */
export interface GpsGate {
  readonly type: QueuedPunchType;
  readonly shiftId: string | null;
  readonly attemptedAt: string;
}

export interface RecoveryView {
  readonly kind: RecoveryKind;
  readonly tone: StatusTone;
  readonly type: QueuedPunchType;
  /** True when the punch is fine and the worker should carry on. */
  readonly informational: boolean;
  readonly refusalCode: ErrorCode | null;
  /** The stored row, for the conditions that name a punch the server refused. */
  readonly failure: FailedItemSummary | null;
  readonly attemptedAt: string | null;
  /** The `clientPunchUuid` `acknowledgeFailed` clears, when there is one. */
  readonly acknowledges: string | null;
}

export interface DeriveRecoveryArgs {
  readonly attempt: RecoveryAttempt | null;
  readonly gpsGate: GpsGate | null;
  readonly failures: readonly FailedItemSummary[];
}

/**
 * Codes that belong to authentication, not to a punch. They can only be
 * produced by `POST /auth/login`, and W1 owns them.
 */
const LOGIN_ONLY: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'ACCOUNT_LOCKED',
  'ACCOUNT_HARD_LOCKED',
  'ACCOUNT_INACTIVE',
  'INVALID_CREDENTIALS',
  'UNAUTHENTICATED',
  'INVALID_REFRESH_TOKEN',
  'REFRESH_TOKEN_REUSED',
  'PIN_CHANGE_REQUIRED',
]);

const KIND_BY_CODE: Partial<Record<ErrorCode, RecoveryKind>> = {
  STALE_OPEN_PUNCH: 'staleOpen',
  OUTSIDE_PUNCH_WINDOW: 'outsideWindow',
  NO_SHIFT_SCHEDULED: 'noShift',
  DEVICE_NOT_REGISTERED: 'deviceNotRegistered',
};

const TONE_BY_KIND: Readonly<Record<RecoveryKind, StatusTone>> = {
  offline: WORKER_STATE_TONE.offline,
  staleOpen: WORKER_STATE_TONE.staleOpenPunch,
  outsideWindow: WORKER_STATE_TONE.outsideWindow,
  noShift: WORKER_STATE_TONE.noShift,
  deviceNotRegistered: WORKER_STATE_TONE.deviceNotRegistered,
  gpsDenied: WORKER_STATE_TONE.gpsDenied,
  syncRefused: WORKER_STATE_TONE.syncRefused,
  refused: WORKER_STATE_TONE.syncRefused,
};

const INFORMATIONAL: ReadonlySet<RecoveryKind> = new Set<RecoveryKind>(['offline', 'gpsDenied']);

/** `true` when this code is a login refusal and must never reach a W8 screen. */
export function isLoginOnlyCode(code: ErrorCode): boolean {
  return LOGIN_ONLY.has(code);
}

export function kindForCode(code: ErrorCode): RecoveryKind {
  return KIND_BY_CODE[code] ?? 'refused';
}

function view(
  kind: RecoveryKind,
  type: QueuedPunchType,
  parts: Partial<Omit<RecoveryView, 'kind' | 'tone' | 'type' | 'informational'>> = {},
): RecoveryView {
  return {
    kind,
    tone: TONE_BY_KIND[kind],
    type,
    informational: INFORMATIONAL.has(kind),
    refusalCode: parts.refusalCode ?? null,
    failure: parts.failure ?? null,
    attemptedAt: parts.attemptedAt ?? null,
    acknowledges: parts.acknowledges ?? null,
  };
}

/**
 * The single recovery screen, or `null` for "the home screen is correct".
 *
 * Precedence, highest first — the order *is* the specification:
 *
 * 1. **The GPS gate.** The worker has asked to punch and is being told what
 *    will be recorded before it is. Nothing else has happened yet.
 * 2. **This attempt's own refusal.** The freshest fact about the tap the
 *    worker just made.
 * 3. **A refusal that arrived on sync**, for any punch the branch above did
 *    not already draw — including the one just attempted, when the tap itself
 *    came back `queued`, `in_flight` or `gone`. It is retained until
 *    acknowledged (I-7) and outranks a merely queued punch, because a queued
 *    punch is fine and a refused one is not.
 * 4. **This attempt stayed queued** — offline, informational.
 */
export function deriveRecovery({
  attempt,
  gpsGate,
  failures,
}: DeriveRecoveryArgs): RecoveryView | null {
  if (gpsGate !== null) {
    return view('gpsDenied', gpsGate.type, { attemptedAt: gpsGate.attemptedAt });
  }

  const unacknowledged = failures.filter((item) => !item.acknowledged);

  if (attempt !== null && attempt.outcome === 'refused' && attempt.refusalCode !== null) {
    const code = attempt.refusalCode;
    // A login refusal reaching a punch is a bug somewhere else; rendering it
    // inside the authenticated shell would be a second one.
    if (!isLoginOnlyCode(code)) {
      const failure =
        unacknowledged.find((item) => item.clientPunchUuid === attempt.clientPunchUuid) ?? null;
      return view(kindForCode(code), attempt.type, {
        refusalCode: code,
        failure,
        attemptedAt: attempt.attemptedAt,
        acknowledges: failure === null ? null : failure.clientPunchUuid,
      });
    }
  }

  /*
   * The attempt's own row is skipped only when the branch above already drew
   * it — that is, when this attempt *is* the refusal. Every other outcome
   * (`queued`, `in_flight`, `gone`: the row was taken over, or already
   * resolved, by a concurrent flush) leaves the worker with no screen of their
   * own, and a refusal filtered out here is a refused punch under an ordinary
   * home screen. I-7.
   */
  const afterTheFact = unacknowledged.find(
    (item) =>
      attempt === null ||
      attempt.outcome !== 'refused' ||
      item.clientPunchUuid !== attempt.clientPunchUuid,
  );
  if (afterTheFact !== undefined && !isLoginOnlyCode(afterTheFact.refusalCode)) {
    return view('syncRefused', afterTheFact.type, {
      refusalCode: afterTheFact.refusalCode,
      failure: afterTheFact,
      attemptedAt: afterTheFact.deviceRecordedAt,
      acknowledges: afterTheFact.clientPunchUuid,
    });
  }

  if (attempt !== null && attempt.outcome === 'queued') {
    return view('offline', attempt.type, { attemptedAt: attempt.attemptedAt });
  }

  return null;
}
